import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  VStack,
  Text,
  useToast,
  Container,
  Heading,
  Link as ChakraLink,
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiUser, FiLock, FiMail } from 'react-icons/fi';
import { LOGO_URL, APP_NAME, PAGE_TITLES } from '../constants/branding';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8889';

  // Set document title when component mounts
  useEffect(() => {
    document.title = PAGE_TITLES.REGISTER;
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Passwords do not match',
        status: 'error',
        duration: 3000,
      });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email.toLowerCase(),
          password: formData.password,
          frontend_url: window.location.origin,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      await response.json();

      toast({
        title: 'Welcome to IT Support Assistant!',
        description: 'Please check your email to verify your account',
        status: 'success',
        duration: 5000,
      });
      navigate('/login');
    } catch (error) {
      toast({
        title: 'Registration failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      position="relative"
      overflow="hidden"
    >
      {/* Background Design */}
      <Box
        position="absolute"
        top="-10%"
        right="-10%"
        width="600px"
        height="600px"
        bg="blue.500"
        borderRadius="full"
        opacity={0.1}
      />
      <Box
        position="absolute"
        bottom="-15%"
        left="-15%"
        width="800px"
        height="800px"
        bg="blue.400"
        borderRadius="full"
        opacity={0.1}
      />

      <Container maxW="sm" position="relative" zIndex={1}>
        <VStack spacing={6} bg="white" p={8} borderRadius="xl" boxShadow="lg">
          <VStack spacing={2} w="full" align="center">
            <Box mb={1}>
              <img 
                src={LOGO_URL}
                alt="IT360 Logo" 
                style={{ height: '50px', width: 'auto' }} 
              />
            </Box>
            <Heading size="lg" color="blue.600">Create Account</Heading>
            <Text color="gray.600" fontSize="md">Join {APP_NAME}</Text>
          </VStack>

          <Box as="form" onSubmit={handleSubmit} w="full">
            <VStack spacing={4}>
              <InputGroup size="md">
                <Input
                  name="username"
                  pl={8}
                  placeholder="Username"
                  value={formData.username}
                  onChange={handleChange}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  fontSize="sm"
                  _focus={{
                    bg: "white",
                    borderColor: "blue.500",
                  }}
                  _hover={{
                    borderColor: "blue.400",
                  }}
                  required
                />
                <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400" zIndex={2}>
                  <FiUser size="14px" />
                </Box>
              </InputGroup>

              <InputGroup size="md">
                <Input
                  name="email"
                  pl={8}
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  fontSize="sm"
                  _focus={{
                    bg: "white",
                    borderColor: "blue.500",
                  }}
                  _hover={{
                    borderColor: "blue.400",
                  }}
                  required
                />
                <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400" zIndex={2}>
                  <FiMail size="14px" />
                </Box>
              </InputGroup>

              <InputGroup size="md">
                <Input
                  name="password"
                  pl={8}
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  fontSize="sm"
                  _focus={{
                    bg: "white",
                    borderColor: "blue.500",
                  }}
                  _hover={{
                    borderColor: "blue.400",
                  }}
                  required
                />
                <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400" zIndex={2}>
                  <FiLock size="14px" />
                </Box>
                <InputRightElement>
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    h="1.5rem"
                    size="xs"
                    onClick={() => setShowPassword(!showPassword)}
                    icon={showPassword ? <FiEyeOff size="14px" /> : <FiEye size="14px" />}
                    variant="ghost"
                    color="gray.400"
                    _hover={{ color: "blue.500" }}
                  />
                </InputRightElement>
              </InputGroup>

              <InputGroup size="md">
                <Input
                  name="confirmPassword"
                  pl={8}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  fontSize="sm"
                  _focus={{
                    bg: "white",
                    borderColor: "blue.500",
                  }}
                  _hover={{
                    borderColor: "blue.400",
                  }}
                  required
                />
                <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400" zIndex={2}>
                  <FiLock size="14px" />
                </Box>
                <InputRightElement>
                  <IconButton
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    h="1.5rem"
                    size="xs"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    icon={showConfirmPassword ? <FiEyeOff size="14px" /> : <FiEye size="14px" />}
                    variant="ghost"
                    color="gray.400"
                    _hover={{ color: "blue.500" }}
                  />
                </InputRightElement>
              </InputGroup>

              <Button
                type="submit"
                colorScheme="blue"
                size="md"
                width="100%"
                isLoading={isLoading}
                py={5}
                mt={2}
                fontSize="sm"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'md',
                }}
                transition="all 0.2s"
              >
                Create Account
              </Button>
            </VStack>
          </Box>

          <Text fontSize="sm" color="gray.600">
            Already have an account?{' '}
            <ChakraLink 
              as={Link} 
              to="/login" 
              color="blue.500"
              fontWeight="semibold"
              _hover={{
                textDecoration: 'none',
                color: 'blue.600'
              }}
            >
              Sign in
            </ChakraLink>
          </Text>
        </VStack>
      </Container>
    </Flex>
  );
};

export default Register; 